<script setup>
import { computed, ref, watch } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  calculateMetaPricing,
  META_CATEGORIES,
  META_PRICING_CHANGE,
} from './metaPricingCalculator';
import {
  usageMonthOptions,
  usageCount,
  formatUsageRate,
  maskUsageRate,
  parseUsageRate,
} from './messageUsageHelpers';

const props = defineProps({
  rates: { type: Object, default: () => ({}) },
  numberName: { type: String, default: '' },
});
const months = usageMonthOptions();
const month = ref(
  months.find(item => item.current).value < META_PRICING_CHANGE
    ? META_PRICING_CHANGE
    : months.find(item => item.current).value
);
const quantities = ref({});
const freeEntry = ref({});
const utilityWindow = ref(0);
const serviceAllowance = ref(0);
const draftRates = ref({});
watch(
  () => props.rates,
  rates => {
    draftRates.value = Object.fromEntries(
      Object.entries(rates).map(([category, value]) => [
        category,
        formatUsageRate(value),
      ])
    );
  },
  { immediate: true }
);
const updated = computed(() => month.value >= META_PRICING_CHANGE);
const result = computed(() =>
  calculateMetaPricing({
    month: month.value,
    quantities: quantities.value,
    freeEntry: freeEntry.value,
    utilityWindow: utilityWindow.value,
    serviceAllowance: serviceAllowance.value,
    rates: Object.fromEntries(
      Object.entries(draftRates.value).map(([key, value]) => [
        key,
        parseUsageRate(value),
      ])
    ),
  })
);
const money = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value
  );
const boundedCount = value =>
  Math.min(100000000, Math.max(0, Math.floor(Number(value) || 0)));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="rounded-2xl border border-n-weak bg-n-solid-1 p-5"
    data-testid="meta-calculator"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 class="flex items-center gap-2 font-medium">
          <span class="i-lucide-calculator size-5 text-n-brand" />Calculadora
          Meta
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          Previsão mensal para um número · {{ numberName }}
        </p>
      </div>
      <ComboBox
        v-model="month"
        :options="months"
        class="min-w-48"
        aria-label="Mês da simulação"
      />
    </div>
    <p class="my-4 text-sm text-n-slate-11">
      {{
        updated
          ? 'A partir de 01/10/2026: serviço e utilidade dentro das 24 horas entram na cobrança por mensagem.'
          : 'Até 30/09/2026: serviço e utilidade dentro das 24 horas são gratuitos.'
      }}
      Informe as entregas previstas, incluindo as gratuitas. A janela de entrada
      gratuita de 72 horas continua isenta.
    </p>
    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <section
        v-for="(label, category) in META_CATEGORIES"
        :key="category"
        class="space-y-3 rounded-xl border border-n-weak p-4"
      >
        <h4 class="font-medium">{{ label }}</h4>
        <Input
          :model-value="quantities[category] || 0"
          :label="`Entregas de ${label.toLowerCase()}`"
          type="number"
          min="0"
          max="100000000"
          step="1"
          @update:model-value="quantities[category] = boundedCount($event)"
        />
        <Input
          :model-value="freeEntry[category] || 0"
          label="Dessas, gratuitas em 72h"
          type="number"
          min="0"
          :max="quantities[category] || 0"
          step="1"
          @update:model-value="freeEntry[category] = boundedCount($event)"
        />
        <Input
          :model-value="draftRates[category] || ''"
          label="Tarifa por mensagem (R$)"
          placeholder="Informe a tarifa da Meta"
          inputmode="decimal"
          @update:model-value="draftRates[category] = maskUsageRate($event)"
        />
      </section>
    </div>
    <div class="my-4 grid gap-4 md:grid-cols-2">
      <div v-if="!updated">
        <Input
          :model-value="utilityWindow"
          label="Utilidade dentro de 24h, fora das 72h gratuitas"
          type="number"
          min="0"
          max="100000000"
          step="1"
          @update:model-value="utilityWindow = boundedCount($event)"
        />
      </div>
      <div v-else>
        <Input
          :model-value="serviceAllowance"
          label="Franquia de serviço confirmada na sua conta (opcional)"
          type="number"
          min="0"
          max="100000000"
          step="1"
          @update:model-value="serviceAllowance = boundedCount($event)"
        />
        <p class="mt-2 text-xs text-n-slate-11">
          Use somente uma franquia confirmada pela Meta. A meta de planejamento
          do relatório não é descontada automaticamente.
        </p>
      </div>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead class="text-xs text-n-slate-11">
          <tr>
            <th class="p-3">Categoria</th>
            <th class="p-3">Gratuitas</th>
            <th class="p-3">Com cobrança</th>
            <th class="p-3">Estimativa</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in result.rows"
            :key="row.category"
            class="border-t border-n-weak"
          >
            <td class="p-3">{{ META_CATEGORIES[row.category] }}</td>
            <td class="p-3 tabular-nums">{{ usageCount(row.free) }}</td>
            <td class="p-3 tabular-nums">{{ usageCount(row.billable) }}</td>
            <td class="p-3 tabular-nums">
              {{
                row.unpriced
                  ? 'Informe a tarifa'
                  : money(row.costMicros / 1000000)
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-n-alpha-1 p-4"
      aria-live="polite"
    >
      <span class="text-sm">{{
        result.unpriced ? 'Total parcial estimado' : 'Total estimado Meta'
      }}</span
      ><strong class="text-xl text-n-brand">{{ money(result.total) }}</strong>
      <p v-if="result.unpriced" class="w-full text-xs text-n-slate-11">
        {{ usageCount(result.unpriced) }} mensagens sem tarifa ficaram fora do
        total.
      </p>
    </div>
    <p class="mt-4 text-xs text-n-slate-11">
      As tarifas configuradas neste número são preenchidas como referência.
      Ajuste conforme o país do destinatário, a moeda e a faixa de volume da sua
      conta. Esta simulação não altera os parâmetros salvos. Não inclui
      assinatura Talkvia, consumo de OpenAI, impostos ou conversão cambial.
      Descontos por volume de utilidade e autenticação devem estar refletidos na
      tarifa média informada; serviço não tem desconto por volume.
    </p>
    <a
      href="https://developers.facebook.com/documentation/business-messaging/whatsapp/pricing/non-template-messages/"
      target="_blank"
      rel="noopener noreferrer"
      class="mt-3 inline-block text-sm text-n-brand underline"
      >Regras de cobrança da Meta</a
    >
  </section>
</template>
